
import { Component, Vue } from 'vue-property-decorator'
import { getToken } from '@/utils/cookies'
import request from '@/utils/request'

@Component({
  name: 'StoreDashboard'
})
export default class extends Vue {
  private loading = false
  private startDate = ''
  private endDate = ''
  private stats = {
    android: 0,
    ios: 0,
    activeEvents: 0,
    problems: 0
  }

  private eventsByType: any[] = []
  private eventsByStatus: any[] = []
  private recentProblems: any[] = []
  private dailyTrends: any[] = []

  created() {
    // Default to last 30 days
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 30)
    this.startDate = this.formatDateForApi(start)
    this.endDate = this.formatDateForApi(end)
    this.loadDashboard()
  }

  private formatDateForApi(date: Date): string {
    return date.toISOString().split('T')[0]
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return 'N/A'
    const date = new Date(dateStr)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${day}.${month}.${year} ${hours}:${minutes}`
  }

  private async loadDashboard() {
    this.loading = true
    try {
      const params: any = {}
      if (this.startDate) {
        params.startDate = this.startDate
      }
      if (this.endDate) {
        params.endDate = this.endDate
      }

      const response: any = await request({
        url: '/admin/subscription/store-dashboard',
        method: 'get',
        params,
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      })

      // Handle both response formats (response.data.success or response.success)
      const data = response.data || response
      if (data.success) {
        const dashboard = data.dashboard

        // Calculate stats
        const androidLog = dashboard.webhookLogs.byPlatform.find((p: any) => p.platform === 'android')
        const iosLog = dashboard.webhookLogs.byPlatform.find((p: any) => p.platform === 'ios')
        this.stats.android = androidLog?.total || 0
        this.stats.ios = iosLog?.total || 0

        const activeEvent = dashboard.events.byStatus.find((s: any) => s.status === 'active')
        this.stats.activeEvents = activeEvent?.total || 0
        this.stats.problems = dashboard.recentProblems.length

        this.eventsByType = dashboard.events.byType || []
        this.eventsByStatus = dashboard.events.byStatus || []
        this.recentProblems = dashboard.recentProblems || []
        this.dailyTrends = dashboard.trends.daily || []
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error)
      this.$message.error('Failed to load dashboard')
    } finally {
      this.loading = false
    }
  }

  private getEventTypeTag(eventType: string): string {
    const typeMap: { [key: string]: string } = {
      purchase: 'success',
      renewal: 'success',
      cancel: 'danger',
      expire: 'warning',
      refund: 'danger',
      renewal_failed: 'danger',
      grace_period: 'warning',
      hold: 'warning',
      pause: 'info',
      reactivate: 'success'
    }
    return typeMap[eventType] || 'info'
  }

  private getStatusTag(status: string): string {
    const statusMap: { [key: string]: string } = {
      active: 'success',
      cancelled: 'danger',
      expired: 'warning',
      in_grace_period: 'warning',
      on_hold: 'warning',
      paused: 'info',
      refunded: 'danger'
    }
    return statusMap[status] || 'info'
  }

  // Human-readable event type labels
  private getEventTypeLabel(eventType: string): string {
    const labelMap: { [key: string]: string } = {
      purchase: 'Initial Purchase',
      renewal: 'Renewal',
      cancel: 'Cancelled',
      expire: 'Expired',
      refund: 'Refunded',
      renewal_failed: 'Renewal Failed',
      grace_period: 'Grace Period',
      hold: 'On Hold',
      pause: 'Paused',
      reactivate: 'Reactivated'
    }
    return labelMap[eventType] || eventType || 'Unknown'
  }

  // Human-readable status labels
  private getStatusLabel(status: string): string {
    const labelMap: { [key: string]: string } = {
      active: 'Active',
      cancelled: 'Cancelled',
      expired: 'Expired',
      in_grace_period: 'Grace Period',
      on_hold: 'On Hold',
      paused: 'Paused',
      refunded: 'Refunded'
    }
    return labelMap[status] || status || 'Unknown'
  }

  // Cancellation reason labels
  private getCancellationReasonLabel(reason: string | null): string {
    if (!reason) return 'N/A'
    const labelMap: { [key: string]: string } = {
      user_cancelled: 'User Cancelled',
      billing_error: 'Billing Error',
      developer_cancelled: 'Developer Cancelled',
      system_cancelled: 'System Cancelled',
      price_increase: 'Price Increase',
      product_not_available: 'Product Not Available'
    }
    return labelMap[reason] || reason
  }
}
